import { Component, OnInit, AfterViewInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Validators, FormControl, FormGroup } from '@angular/forms';
import { CreateAnalysisDefinitionRequest, AnalysisDefinition, AnalysisDefinitionsService } from '@stratus/gss-ng-sdk';
import { NgxSmartModalService, ToastrService, NgxSmartModalComponent, DataTableComponent, GridOptions, ProgressComponent } from '@bssh/comp-lib';
import { BaseModalComponent } from '@app/shared/modals/base-modal/base-modal.component';
import { IEditAnalysisDefinitionModalInput } from '../../model/action-modal';
import { CustomColDef } from '@app/shared/bssh-data-table/custom-colDef';
import { get } from 'lodash';
import { GridApi } from 'ag-grid-community';
import { DeleteButtonCellRendererComponent } from '@app/resources/shared/delete-button-cell-renderer/delete-button-cell-renderer.component';
import { GetResourcesRoute, ResourcesPath } from '@app/resources/constants';
import { Router } from '@angular/router';

@Component({
  selector: 'analysis-definition-modal',
  templateUrl: './analysis-definition-modal.component.html',
  styleUrls: ['./analysis-definition-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class AnalysisDefinitionModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
  public modalType = 'analysisDefinition';
  // Modal Text
  public title = 'Custom Analysis Definition';
  public closeButtonText = 'Cancel';
  public confirmButtonText = 'Save';

  public columnDefs: CustomColDef[];
  public gridOptions: GridOptions;

  private analysisDefinition: AnalysisDefinition;
  public get isAnalysisDefinitionIllumina(): boolean {
    return this.analysisDefinition ? this.analysisDefinition.isIllumina : false;
  }

  public get isAddVersionDisabled(): boolean {
    return !this.analysisDefinition || !this.analysisDefinition.id;
  }

  @ViewChild('loadingSpinner', { static: true })
  loadingSpinner: ProgressComponent;
  isLoading: boolean;
  
  constructor(private analysisDefinitionsService: AnalysisDefinitionsService,
    public ngxSmartModalService: NgxSmartModalService,
    private toastr: ToastrService,
    private router: Router) {
    super(ngxSmartModalService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initializeAnalysisVersionTable();
    this.subs.sink = this.data.subscribe(
      (input: IEditAnalysisDefinitionModalInput) => {
        this.analysisDefinition = input ? input.analysisDefinition : null;
        // fill up both UI and text editors so that users will see 
        // content even if there is an error converting between the two
        this.buildFormGroup(this.analysisDefinition);
        this.stopSpinning();
      }
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  // avd Form validation rules
  public ValidationRule = {
    commonText: {
      maxLength: 255,
    },
    description: {
      maxLength: 8192
    }
  }

  public ValidationMessages = {
    commonInputTextField: {
      maxlength: `Max ${this.ValidationRule.commonText.maxLength} characters.`
    },
    name: {
      required: 'Name is required.'
    },
    displayName: {
      required: 'Display Name is required.'
    },
    description: {
      maxlength: `Max ${this.ValidationRule.description.maxLength} characters.`
    }
  }

  // Initialize form with validations
  public avdFormGroup: FormGroup = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.maxLength(this.ValidationRule.commonText.maxLength)
    ]),
    displayName: new FormControl('', [
      Validators.required,
      Validators.maxLength(this.ValidationRule.commonText.maxLength)
    ]),
    organization: new FormControl('', [
      Validators.maxLength(this.ValidationRule.commonText.maxLength)
    ]),
    description: new FormControl('', [
      Validators.maxLength(this.ValidationRule.description.maxLength)
    ])
  });

  public getErrorMessage(controlName: string): string {
    const control = this.avdFormGroup.get(controlName);
    if (!control || !control.touched || !control.errors) {
      return null;
    }
    if (control.errors.maxlength) {
      return controlName === 'description' ? this.ValidationMessages.description.maxlength : this.ValidationMessages.commonInputTextField.maxlength;
    }
    if (control.errors.required) {
      return this.ValidationMessages[controlName].required;
    }
    return "";
  }

  public buildFormGroup(analysisDefinition: AnalysisDefinition) {
    if (analysisDefinition != null) {
      this.avdFormGroup.patchValue({
        name: analysisDefinition.name,
        displayName: analysisDefinition.displayName,
        organization: analysisDefinition.organization,
        description: analysisDefinition.description
      });
    }
    if (!this.isAnalysisDefinitionIllumina) {
      this.avdFormGroup.enable();
    } else {
      // Disable form if is illumina standard analysis definition
      this.avdFormGroup.disable();
    }
  }

  // Initialize analysis version table
  // TODO: add link to analysis version details page
  // TO Discuss: do we need to separate custom and standard analysis versions?
  public initializeAnalysisVersionTable() {
    this.columnDefs = [
      {
        headerName: 'Version Name',
        field: 'displayVersion',
        cellRenderer: 'link',
        cellRendererParams: (params) => {
            if (params.data) {
                const cellValue = params.data.displayVersion ? params.data.displayVersion : params.data.name;
            const href = params.data.editorHref
              ? params.data.editorHref
              : `/resources/${ResourcesPath.AnalysisDefinitions}/${params.data.id}`;
                return {
              value: cellValue,
              href
                };
            }

            return null;
        },
        suppressSizeToFit: false,
        minWidth: 100,
        checkboxSelection: false,
        resizable: true
      },
      {
        headerName: 'Description',
        field: 'description',
        cellRenderer: 'span',
        suppressSizeToFit: false,
        minWidth: 100,
        resizable: true
      },
      {
        headerName: '',
        field: 'isIllumina',
        maxWidth: 50,
        cellRendererFramework: DeleteButtonCellRendererComponent,
        cellRendererParams: {
          deleteHandler: this.onDeleteAnalysisVersionDefinition.bind(this),
          canDeleteRow: () => !this.isAnalysisDefinitionIllumina,
        },
        type: 'nonSortFilterColumn',
        suppressSizeToFit: true
      }
    ];

    this.gridOptions = {
      pagination: true,
      rowSelection: 'none',
      columnDefs: this.columnDefs,
      context: {
        componentParent: this
      },
      paginationPageSize: 20,
      cacheBlockSize: 20
    };
  }

  public onGridReady(gridApi: GridApi) {
    gridApi.sizeColumnsToFit();
    this.gridOptions.api = gridApi;
    // Get data from analysis definition
    this.gridOptions.api.setRowData(get(this.analysisDefinition, 'analysisVersions.items', []));
  }

  public onAddVersionClick(){
    if (!this.analysisDefinition || !this.analysisDefinition.id) {
      return;
    }
    const target = `${GetResourcesRoute(ResourcesPath.AnalysisDefinitions)}/versions/${ResourcesPath.CreateNew}`;
    const queryParams = { analysisDefinitionId: this.analysisDefinition.id };
    this.ngxSmartModalService.close(this.modalType);
    this.router.navigate([target], { queryParams });
  }
  
  public onDeleteAnalysisVersionDefinition(data: any) {
    // TODO: to implement
    console.log('Delete analysis version:', data);
  }

  /**
   * Upon submission, process the form and submit request to GSS to create avd and handle return result
   */
  accept(modal: NgxSmartModalComponent) {
    this.startSpinning();
    const createAnalysisDefinitionRequest: CreateAnalysisDefinitionRequest = {
      name: this.avdFormGroup.value.name,
      displayName: this.avdFormGroup.value.displayName,
      organization: this.avdFormGroup.value.organization,
      description: this.avdFormGroup.value.description,
      status: 'Active' // Default to Active status
    };
    const request = this.analysisDefinition ?
      this.analysisDefinitionsService.updateCustomAnalysisDefinition({
        analysisDefinitionId: this.analysisDefinition.id,
        body: createAnalysisDefinitionRequest
      }) : this.analysisDefinitionsService.createCustomAnalysisDefinition(createAnalysisDefinitionRequest);
    this.subs.sink = request.subscribe({
      next: iakEdited => this.processSuccessfulAdEdit(iakEdited, modal),
      error: response => this.parseErrorResponse(response.error)
    });
  }

  /**
   * Prompt successful toast message, emit the event and close the modal.
   * @param analysisDefinitionCreated created ad data returned from GSS
   * @param modal instance needed to close the modal
   */
  private processSuccessfulAdEdit(
    analysisDefinitionCreated: AnalysisDefinition,
    modal: NgxSmartModalComponent
  ) {
    this.stopSpinning();
    this.toastr.success(
      `Your analysis definition: '${analysisDefinitionCreated.displayName}' has been successfully saved.`
    );
    this.confirm.emit();
    modal.close();
  }

  private parseErrorResponse(errorResponse: any) {
    this.stopSpinning();
    const innerErrorMessage = this.getInnerErrorMessage(errorResponse);
    this.toastr.error(innerErrorMessage || 'An error occurred while saving the analysis definition. Please try again.');
  }

  private getInnerErrorMessage(errorResponse: any): string {
    if(errorResponse.details) {
      return errorResponse.details.map(detail => Object.values(detail).join('. ')).join(' ');
    }
    return errorResponse.message || '';
  }
  
  private startSpinning() {
    this.isLoading = true;
    this.loadingSpinner.start();
  }

  private stopSpinning() {
    this.isLoading = false;
    this.loadingSpinner.complete();
  }

}
